import React, { useEffect, useState } from 'react';
import ReportingPageLayout from './ReportingPageLayout';
import useReportingPage from '../hooks/useReportingPage';
import { STAFFING_REPORTS, STAFFING_REPORT_KEYS } from '../constants/staffingReportsConfig';
import { currencyFormatter } from '../utils/reportingUtils';

const axios = require('axios').default;

const hours = (value) => `${(value || 0).toFixed(1)} h`;
const count = (n, noun) => `${n || 0} ${noun}${n === 1 ? '' : 's'}`;

// The prototype's landing cards: a headline number and what it counts, from the same totals as each report.
const headline = (key, summary, showWages) => {
  const s = summary[key] || {};
  switch (key) {
    case 'labor_by_event':
      return showWages ? [currencyFormatter.format(s.labor_cost || 0), 'labor this period'] : [hours(s.actual_hours), 'worked this period'];
    case 'labor_by_position': return [String(s.positions || 0), s.positions === 1 ? 'position worked' : 'positions worked'];
    case 'hours_by_staff': return [hours((s.actual_hours || 0) + (s.volunteer_hours || 0)), s.people === 1 ? '1 person' : `${s.people || 0} people`];
    case 'volunteer_hours': return [hours(s.hours), count(s.volunteers, 'volunteer')];
    case 'attendance': return [String(s.issues || 0), 'issues this period'];
    default: return ['', ''];
  }
};

function ReportingStaffingPage({ team, csrfToken, venueOwnerships, showWages }) {
  const {
    isMobile, sidebarOpen, setSidebarOpen,
    dateRange, setDateRange,
    filterSidebarOpen, setFilterSidebarOpen,
    activeFilters,
    handleFilterChange, handleRemoveFilter, handleClearAllFilters,
  } = useReportingPage('this_month', { urlFilterIds: ['venue_ownership_id'] });
  const [summary, setSummary] = useState(null);
  const [summaryFailed, setSummaryFailed] = useState(false);
  const teamId = team?.id;

  useEffect(() => {
    if (!teamId) return undefined;
    if (csrfToken) axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    let ignore = false;
    // All Time sends no dates.
    const params = { start_date: dateRange?.start || undefined, end_date: dateRange?.end || undefined, venue_ownership_id: activeFilters.venue_ownership_id };
    axios.get(`/teams/${teamId}/reporting/staffing/summary.json`, { params })
      .then((res) => { if (!ignore) { setSummary(res.data || {}); setSummaryFailed(false); } })
      .catch((err) => {
        console.error('Failed to load the staffing summary', err);
        if (!ignore) { setSummary(null); setSummaryFailed(true); }
      });
    return () => { ignore = true; };
  }, [teamId, csrfToken, dateRange?.start, dateRange?.end, activeFilters.venue_ownership_id]);

  const filterGroups = [{
    id: 'venue',
    label: 'Venue',
    filters: [{
      id: 'venue_ownership_id',
      label: 'Venue',
      type: 'checkbox',
      options: (venueOwnerships || []).map(o => ({ value: String(o.id), label: o.name })),
    }],
  }];

  return (
    <ReportingPageLayout
      team={team}
      activeKey="staffing"
      title="Staffing"
      subtitle="Hours come from approved and pending time cards."
      dateRange={dateRange}
      setDateRange={setDateRange}
      isMobile={isMobile}
      sidebarOpen={sidebarOpen}
      setSidebarOpen={setSidebarOpen}
      filterSidebarOpen={filterSidebarOpen}
      setFilterSidebarOpen={setFilterSidebarOpen}
      activeFilters={activeFilters}
      filterGroups={filterGroups}
      handleFilterChange={handleFilterChange}
      handleRemoveFilter={handleRemoveFilter}
      handleClearAllFilters={handleClearAllFilters}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        {STAFFING_REPORT_KEYS.map((key) => {
          const report = STAFFING_REPORTS[key];
          const [number, numberLabel] = summary ? headline(key, summary, showWages) : ['—', summaryFailed ? 'Couldn\'t load totals' : ''];
          return (
            <a
              key={key}
              href={teamId ? `/teams/${teamId}/reporting/staffing/${key}` : '#'}
              className="text-decoration-none d-block"
              style={{ background: 'var(--sl-bg-primary, #fff)', border: '1px solid var(--sl-border-secondary, #e4e7ec)', borderRadius: 8, padding: 20, color: 'inherit' }}
            >
              <div style={{ fontWeight: 700, fontSize: 16, color: 'var(--sl-text-primary, #101828)', marginBottom: 6 }}>
                {report.title}
              </div>
              <div style={{ fontSize: 13, color: 'var(--sl-text-quaternary, #667085)', lineHeight: 1.5, marginBottom: 16 }}>
                {report.description}
              </div>
              <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--sl-text-primary, #101828)', lineHeight: 1.2 }}>{number}</div>
              <div style={{ fontSize: 13, color: 'var(--sl-text-tertiary, #667085)' }}>{numberLabel}</div>
            </a>
          );
        })}
      </div>
    </ReportingPageLayout>
  );
}

export default ReportingStaffingPage;
